import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  IconButton,
  Modal,
} from "@mui/material";
import { motion } from "framer-motion";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import CloseIcon from "@mui/icons-material/Close";

import primaryImg from "../images/primary.jpeg";
import secondaryImg from "../images/secondary.jpg";
import coldChainImg from "../images/coldChainImg.jpg";
import expressImg from "../images/express.jpg";
import India from "../images/indiamapn.jpg";

const services = [
  {
    title: "Primary Distribution",
    description:
      "At Mangalmurti Transport, primary distribution forms the backbone of our logistics services, ensuring the smooth movement of goods from manufacturing units or import terminals to regional warehouses, depots, and distribution centers. We specialize in long-haul operations using full truckload (FTL), less than truckload (LTL), and bulk cargo transportation, depending on the type and volume of goods. Our focus is on delivering efficiency through optimized routing, maximum vehicle utilization, and strict adherence to safety and compliance standards. By connecting manufacturers directly with distribution hubs, we provide a reliable foundation for the supply chain, from which secondary distribution ensures timely deliveries to retailers and end customers.",
    image: primaryImg,
    icon: <LocalShippingIcon sx={{ fontSize: 40, color: "#fff" }} />,
  },
  {
    title: "Secondary Distribution",
    description:
      "At Mangalmurti Transport, secondary distribution focuses on the movement of goods from regional warehouses, depots, or distribution centers to retailers, wholesalers, and end customers. This stage of logistics is crucial for ensuring that products reach the right market on time and in perfect condition. We manage smaller load sizes, frequent deliveries, and last-mile connectivity with precision and reliability. By offering flexible scheduling, route optimization, and real-time tracking, we ensure efficient secondary distribution that supports businesses in maintaining steady supply chains and meeting customer demand effectively.",
    image: secondaryImg,
    icon: <AccessTimeIcon sx={{ fontSize: 40, color: "#fff" }} />,
  },
  {
    title: "Cold Chain Logistics",
    description:
      "At Mangalmurti Transport, we provide reliable cold chain logistics solutions designed to maintain the quality and safety of temperature-sensitive goods throughout the supply chain. From pharmaceuticals and medical supplies to perishable food products, our refrigerated vehicles and specialized handling processes ensure that products remain within the required temperature range during storage and transportation. With real-time monitoring, efficient route planning, and strict compliance with safety standards, we guarantee that every shipment arrives fresh, safe, and on time. Our cold chain services give businesses the confidence to transport sensitive products without compromising on quality.",
    image: coldChainImg,
    icon: <AcUnitIcon sx={{ fontSize: 40, color: "#fff" }} />,
  },
  {
    title: "Express Delivery",
    description:
      "At Mangalmurti Transport, our Express Delivery services are designed for businesses that require fast, time-bound, and reliable transportation of goods. We specialize in priority shipments, ensuring that urgent consignments are picked up and delivered within the shortest possible time frame. With dedicated vehicles, optimized routes, and real-time tracking, we guarantee speed without compromising on safety or efficiency. Whether it’s critical documents, essential supplies, or high-value goods, our express delivery solutions provide customers with the assurance of timely and secure deliveries every time.",
    image: expressImg,
    icon: <FlashOnIcon sx={{ fontSize: 40, color: "#fff" }} />,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Services() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeIndex, setActiveIndex] = useState(null); //

  return (
    <Box
      id="services"
      sx={{ py: { xs: 5, md: 8 }, backgroundColor: "#f9f9f9" }}
    >
      <Container>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <Typography
            variant="h4"
            align="center"
            fontWeight="bold"
            gutterBottom
            sx={{ color: "#e64a14", fontSize: { xs: "1.8rem", md: "2.3rem" } }}
          >
            Our Services
          </Typography>

          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            mb={6}
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              maxWidth: 700,
              mx: "auto",
            }}
          >
            Comprehensive transport solutions tailored to your business needs —
            reliable, timely, and safe delivery every time.
          </Typography>

          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item xs={12} key={index}>
                <motion.div variants={itemVariants}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        md: index % 2 === 0 ? "row" : "row-reverse", // alternate layout
                      },
                      minHeight: 260, // reduced size
                      borderRadius: "15px",
                      overflow: "hidden",
                      boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
                      backgroundColor: "#fff",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 10px 35px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        flex: 1,
                        backgroundImage: `url(${service.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        minHeight: { xs: 180, md: "auto" },
                      }}
                    />

                    {/* Content */}
                    <Box
                      sx={{
                        flex: 1,
                        p: { xs: 2, md: 3 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      {service.icon}
                      <Typography
                        fontWeight="bold"
                        mt={1}
                        sx={{
                          fontSize: { xs: "1.1rem", md: "1.4rem" },
                          color: "#0e2b4a",
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          color: "#444",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {service.description}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          mt: 2,
                          background:
                            "linear-gradient(90deg, #ff5b1f, #ff8146)",
                          textTransform: "none",
                          fontWeight: "bold",
                          alignSelf: "flex-start",
                          borderRadius: "8px",
                          "&:hover": {
                            background:
                              "linear-gradient(90deg, #e64a14, #ff6530)",
                          },
                        }}
                        onClick={() => setActiveIndex(index)}
                      >
                        Read More
                      </Button>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Modal */}
      <Modal
        open={activeIndex !== null}
        onClose={() => setActiveIndex(null)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: { xs: "90%", md: "60%" },
            maxHeight: "80vh",
            overflowY: "auto",
            backgroundColor: "#fff",
            borderRadius: "15px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            p: 4,
            position: "relative",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setActiveIndex(null)}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon />
          </IconButton>

          {activeIndex !== null && (
            <>
              {services[activeIndex].icon}
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#0e2b4a", mb: 2 }}
              >
                {services[activeIndex].title}
              </Typography>
              <Typography variant="body1" sx={{ color: "#444" }}>
                {services[activeIndex].description}
              </Typography>
            </>
          )}
        </Box>
      </Modal>

      {/* Heading */}
      <Typography
        variant="h4"
        fontWeight="bold"
        gutterBottom
        sx={{ textAlign: "center", letterSpacing: 2, mt: 6 }}
      >
        <span
          style={{
            background: "linear-gradient(90deg, #ff5722, #138808)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontWeight: "bold",
            fontSize: "2rem",
          }}
        >
          WE PRESENCE IN INDIA
        </span>
      </Typography>

      {/* Map */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 700,
          mx: "auto",
          mt: 2,
          aspectRatio: "3 / 4", // keep same as image ratio
          backgroundImage: `url(${India})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        {[
          { id: 1, city: "Mumbai", top: "60%", left: "18%" },
          { id: 2, city: "Pune", top: "61%", left: "28%" },
          { id: 3, city: "Bangalore", top: "76%", left: "26%" },
          { id: 4, city: "Hyderabad", top: "68%", left: "41%" },
          { id: 5, city: "Chennai", top: "82%", left: "38%" },
          { id: 6, city: "Ahmedabad", top: "49%", left: "13%" },
          { id: 7, city: "Delhi", top: "32%", left: "29%" },
          { id: 8, city: "Kochi", top: "87%", left: "29%" },
        ].map((loc) => (
          <Box
            key={loc.id}
            sx={{
              position: "absolute",
              top: loc.top,
              left: loc.left,
              transform: "translate(-50%, -50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Pulse Ring */}
            <motion.div
              animate={{ scale: [1, 1.6, 1], opacity: [1, 0.6, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 87, 34, 0.4)",
                position: "absolute",
              }}
            />

            {/* Dot */}
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#ff5722",
                border: "1px solid #fff",
              }}
            />

            {/* Label */}
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                mt: 0.3,
                fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.75rem" },
                fontWeight: 600,
                backgroundColor: "rgba(0,0,0,0.55)",
                px: { xs: 0.3, sm: 0.5 },
                py: { xs: 0.1, sm: 0.2 },
                borderRadius: "4px",
                whiteSpace: "nowrap",
              }}
            >
              {loc.city}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}